import { DataTable, EmptyState, Pagination, useSpinner } from '@chikara/ui';
import type { Statement } from '@/features/credits/types/credits.types';
import emptyListIconUrl from '@/features/card/assets/empty-list-icon.svg';
import styles from './StatementsSection.module.css';
import { t } from '@/i18n';

export interface StatementsSectionProps {
  statements: Statement[];
  pagination?: any;
  onDownloadStatement?: (statement: Statement) => void;
}

function DownloadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M10 13V3M10 13L6 9M10 13L14 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 16v1a1 1 0 001 1h12a1 1 0 001-1v-1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function StatementsSection({
  statements,
  pagination,
  onDownloadStatement
}: StatementsSectionProps) {
  const { isSpinnerLoading } = useSpinner();
  const columns = [
    { key: 'period', label: t('credits.periodLabel'), align: 'left' as const },
    { key: 'actions', label: t('credits.actionsLabel'), align: 'right' as const },
  ];

  const tableData = statements.map((s) => ({
    period: s.label,
    actions: s.period,
    _row: s,
  }));

  const renderCell = (
    key: string,
    value: any,
    row: Record<string, any>
  ): React.ReactNode => {
    if (key === 'actions') {
      const statement = (row as { _row?: Statement })._row;
      if (!statement) return null;
      return (
        <button
          type="button"
          className={styles.downloadButton}
          onClick={(e) => {
            e.stopPropagation();
            onDownloadStatement?.(statement);
          }}
          aria-label={t('credits.downloadStatement', { period: statement.period })}
        >
          <DownloadIcon />
        </button>
      );
    }
    return (value as React.ReactNode) ?? '-';
  };
  
  return (
    <section className={styles.section} aria-labelledby="statements-title">
      <h2 id="statements-title" className={styles.sectionTitle}>
        {t('credits.statementsTitle')}
      </h2>

      {(statements.length === 0 && !isSpinnerLoading) ? (
        <EmptyState 
          title={t('credits.statementsEmptyTitle')}
          description={t('credits.statementsEmptyDescription')}
          imgSrc={emptyListIconUrl}
        />
      ) : (
        <div className={styles.tableWrapper}>
          <DataTable
            columns={columns}
            data={tableData}
            isLoading={isSpinnerLoading}
            renderCell={renderCell}
          />
          {pagination && <Pagination 
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={(page) => pagination.onPageChange(page, pagination.itemsPerPage)}
            showInfo
            infoText={t('dashboard.paginationInfo', {
                start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                total: pagination.totalItems
            })}
          />}
        </div>
      )}
    </section>
  );
}

StatementsSection.displayName = 'StatementsSection';
